import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { trackLogin, trackRegister } from '@/utils/analytics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage if available
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      console.log('[AUTH] Initial user from localStorage:', savedUser ? 'found' : 'not found');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error('[AUTH] Error parsing user from localStorage:', e);
      return null;
    }
  });
  const [token, setToken] = useState(() => {
    const savedToken = localStorage.getItem('token');
    console.log('[AUTH] Initial token from localStorage:', savedToken ? 'found' : 'not found');
    return savedToken;
  });
  // FIXED: If we have user data in localStorage, don't show loading
  const [loading, setLoading] = useState(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
    // Only show loading if we have a token but no user (need to fetch)
    // If we have both or neither, no loading needed initially
    return savedToken && !savedUser;
  });

  const fetchUser = useCallback(async (tokenToUse) => {
    const currentToken = tokenToUse || token;
    if (!currentToken) {
      console.log('[AUTH] No token, skipping fetchUser');
      setLoading(false);
      return;
    }
    
    // Fast path: seed UI from cache instantly, then ALWAYS re-fetch from
    // the server so subscription changes made by admin between two visits
    // (e.g. Taxi upgrade) reach the client without needing logout/login.
    const savedUser = localStorage.getItem('user');
    if (savedUser && !tokenToUse) {
      try {
        const userData = JSON.parse(savedUser);
        if (userData && userData.email) {
          console.log('[AUTH] Seeding from cached user:', userData.email);
          setUser(userData);
          setLoading(false);
          // fall through — still hit /auth/me below to pick up server-side
          // subscription/role changes.
        }
      } catch (e) {
        console.error('[AUTH] Error parsing saved user:', e);
      }
    }
    
    try {
      console.log('[AUTH] Fetching fresh user with token...');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      console.log('[AUTH] fetchUser success:', response.data?.email);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('[AUTH] Failed to fetch user:', error.response?.status, error.message);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('[AUTH] Auth error, clearing tokens');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
    
    // Listen for user updates from other components (e.g., Google OAuth callback)
    const handleUserUpdate = (event) => {
      if (event.detail) {
        setUser(event.detail);
        // Don't sync token here - it causes double refresh
      }
    };
    
    // Listen for storage changes (when token is set in another tab or by callback)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          setToken(e.newValue);
        } else {
          setToken(null);
          setUser(null);
        }
      }
    };
    
    window.addEventListener('userUpdated', handleUserUpdate);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [token, fetchUser]);

  useEffect(() => {
    if (!token) return;
    const ping = () => {
      if (document.visibilityState !== 'visible') return;
      axios.post(`${API}/auth/ping`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 120000);
    document.addEventListener('visibilitychange', ping);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', ping); };
  }, [token]);

  const login = async (email, password, language = 'en') => {
    try {
      console.log('[AUTH] Attempting login...', { email, api: `${API}/auth/login` });
      const response = await axios.post(`${API}/auth/login`, { email, password, language }, { timeout: 15000 });
      console.log('[AUTH] Login response:', response.data);
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      console.log('[AUTH] Login successful, user set:', userData);
      trackLogin('email');
      return userData;
    } catch (error) {
      console.error('[AUTH] Login error:', error);
      throw error;
    }
  };

  const register = async (name, email, password, language = 'en') => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, language });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    trackRegister('email');
    return userData;
  };

  const logout = () => {
    trackLogin('logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateLanguage = async (language) => {
    if (token) {
      await axios.put(`${API}/auth/language?language=${language}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    }
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser(token);
    }
  };

  // Direct token setter for OAuth callbacks
  const setTokenDirectly = (newToken, userData) => {
    console.log('[AUTH] setTokenDirectly called', { token: newToken ? 'present' : 'missing', user: userData?.email });
    try {
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);
      console.log('[AUTH] setTokenDirectly success, user is now:', userData?.email);
    } catch (e) {
      console.error('[AUTH] setTokenDirectly error:', e);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updateLanguage,
      refreshUser,
      setTokenDirectly,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
